import React, { useEffect, useState } from 'react';
import { Card, Statistic, Row, Col, Button, Spin, Select, DatePicker, Space, message } from 'antd';
import { getAnalyticsStats } from '../../api/api';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

const periodOptions = [
  { label: "24 H", value: "24h" },
  { label: "7 Days", value: "7d" },
  { label: "30 Days", value: "30d" },
  { label: "Date Range", value: "custom" },
];

type PeriodType = "24h" | "7d" | "30d" | "custom";

const LandingPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodType>('24h');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    let fetchStats;
    if (period === 'custom' && dateRange && dateRange[0] && dateRange[1]) {
      fetchStats = getAnalyticsStats(undefined, dateRange[0].toISOString(), dateRange[1].toISOString());
    } else if (period !== 'custom') {
      fetchStats = getAnalyticsStats(period as any);
    } else {
      setLoading(false);
      return;
    }
    fetchStats
      .then(setStats)
      .catch(() => message.error('Failed to load analytics stats.'))
      .finally(() => setLoading(false));
  }, [period, dateRange]);

  // Period select logic
  const handlePeriodChange = (value: PeriodType) => {
    setPeriod(value);
    if (value !== 'custom') setDateRange(null);
  };

  // Range picker logic
  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates);
  };

  const handleClearFilters = () => {
    setPeriod('24h');
    setDateRange(null);
  };

  return (
    <div className="admin-content-container">
      <div className="page-header-row">
        <h2 className="page-title">Key Metrics</h2>
        <Space>
          <Select
            options={periodOptions}
            value={period}
            onChange={handlePeriodChange}
            style={{ width: 140, fontWeight: 600 }}
            size="middle"
            bordered={false}
          />
          <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            disabled={period !== 'custom'}
            allowEmpty={[false, false]}
            format="YYYY-MM-DD"
            style={{ width: 220 }}
            inputReadOnly
            allowClear
            placeholder={["From Date", "To Date"]}
            size="middle"
          />
          <Button type="link" onClick={handleClearFilters} style={{ fontSize: 13, padding: 0 }}>
            Reset
          </Button>
        </Space>
      </div>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {loading ? (
          <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
        ) : (
          <>
            <Row gutter={[12, 12]} justify="center">
              <Col xs={24} sm={12} md={6}>
                <Card className="blueCard card"><Statistic title="Total Sessions" value={stats?.total_sessions} /></Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="greenCard card"><Statistic title="Total Questions" value={stats?.total_questions} /></Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="yellowCard card"><Statistic title="Avg. Questions/Session" value={stats?.avg_questions_per_session} precision={2} /></Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="redCard card"><Statistic title="Avg. Follow-ups/Session" value={stats?.avg_followups_per_session} precision={2} /></Card>
              </Col>
            </Row>
            <Row gutter={[12, 12]} justify="center">
              <Col xs={24} sm={8}>
                <Card className="greenCard card"><Statistic title="Docs Processed" value={stats?.docs_processed} /></Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card className="redCard card"><Statistic title="Docs Failed" value={stats?.docs_failed} valueStyle={{ color: 'red' }} /></Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card className="blueCard card"><Statistic title="Docs Queued" value={stats?.docs_queued} /></Card>
              </Col>
            </Row>
          </>
        )}
        <Button
          type="primary"
          size="large"
          block
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: 24, fontWeight: 600, fontSize: 18 }}
        >
          View Detailed Dashboard
        </Button>
      </Space>
    </div>
  );
};

export default LandingPage;
